import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ClipboardCopy, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { serverUrl } from "@/utils/server-url";
import { queryClient, trpc } from "@/utils/trpc";

const GENERAL_NOTE_VALUE = "general";

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function getUpdateSuccessMessage(variables: {
  title?: string;
  siteId?: string | null;
}): string {
  if (variables.title !== undefined && variables.siteId !== undefined) {
    return "Note updated";
  }

  if (variables.title !== undefined) {
    return "Title updated";
  }

  if (variables.siteId !== undefined) {
    return variables.siteId ? "Assigned to site" : "Marked as general note";
  }

  return "Note updated";
}

export default function NoteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editTitle, setEditTitle] = useState("");
  const [selectedSiteValue, setSelectedSiteValue] =
    useState(GENERAL_NOTE_VALUE);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const recordingQuery = useQuery({
    ...trpc.recordings.getRecording.queryOptions({ id: id ?? "" }),
    enabled: !!id,
  });

  const managedSitesQuery = useQuery({
    ...trpc.getManagedSites.queryOptions(),
  });

  const updateMutation = useMutation({
    ...trpc.recordings.updateRecording.mutationOptions(),
    onSuccess: (_updated, variables) => {
      toast.success(getUpdateSuccessMessage(variables));
      queryClient.invalidateQueries({
        queryKey: trpc.recordings.getRecording.queryOptions({ id: id ?? "" })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: trpc.recordings.listRecordings.queryOptions({}).queryKey,
      });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update");
    },
  });

  const deleteMutation = useMutation({
    ...trpc.recordings.deleteRecording.mutationOptions(),
    onSuccess: () => {
      toast.success("Recording deleted");
      queryClient.invalidateQueries({
        queryKey: trpc.recordings.listRecordings.queryOptions({}).queryKey,
      });
      navigate("/notes");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    },
  });

  const approveSummaryMutation = useMutation({
    ...trpc.recordings.approveSummary.mutationOptions(),
    onSuccess: () => {
      toast.success("Summary approved and recorded in audit log.");
      queryClient.invalidateQueries({
        queryKey: trpc.recordings.getRecording.queryOptions({ id: id ?? "" })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: trpc.recordings.listRecordings.queryOptions({}).queryKey,
      });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to approve");
    },
  });

  const rejectSummaryMutation = useMutation({
    ...trpc.recordings.rejectSummary.mutationOptions(),
    onSuccess: () => {
      toast.success("Summary rejected.");
      queryClient.invalidateQueries({
        queryKey: trpc.recordings.getRecording.queryOptions({ id: id ?? "" })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: trpc.recordings.listRecordings.queryOptions({}).queryKey,
      });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to reject");
    },
  });

  useEffect(() => {
    if (recordingQuery.data?.title) {
      setEditTitle(recordingQuery.data.title);
    }
  }, [recordingQuery.data?.title]);

  useEffect(() => {
    setSelectedSiteValue(recordingQuery.data?.siteId ?? GENERAL_NOTE_VALUE);
  }, [recordingQuery.data?.siteId]);

  if (recordingQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (recordingQuery.error) {
    return (
      <p className="text-sm text-muted-foreground">
        {recordingQuery.error instanceof Error
          ? recordingQuery.error.message
          : "Failed to load recording."}
      </p>
    );
  }

  if (!recordingQuery.data) {
    return (
      <p className="text-sm text-muted-foreground">Recording not found.</p>
    );
  }

  const rec = recordingQuery.data;
  const isProcessing = rec.status === "processing";
  const isPendingApproval = rec.status === "pending_approval";
  const audioSrc = `${serverUrl}/api/recordings/${rec.id}/audio`;

  function saveTitle(nextTitle: string) {
    const trimmedTitle = nextTitle.trim();

    if (!trimmedTitle) {
      setEditTitle(rec.title);
      return;
    }

    if (trimmedTitle === rec.title) {
      setEditTitle(rec.title);
      return;
    }

    updateMutation.mutate({ id: rec.id, title: trimmedTitle });
  }

  function saveSite(nextSiteValue: string) {
    const siteId = nextSiteValue === GENERAL_NOTE_VALUE ? null : nextSiteValue;

    if (siteId === rec.siteId) {
      setSelectedSiteValue(rec.siteId ?? GENERAL_NOTE_VALUE);
      return;
    }

    setSelectedSiteValue(nextSiteValue);
    updateMutation.mutate(
      { id: rec.id, siteId },
      {
        onError: () => {
          setSelectedSiteValue(rec.siteId ?? GENERAL_NOTE_VALUE);
        },
      }
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Input
            value={editTitle}
            onChange={(event) => setEditTitle(event.target.value)}
            onBlur={(event) => saveTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.currentTarget.blur();
              }

              if (event.key === "Escape") {
                setEditTitle(rec.title);
                event.currentTarget.blur();
              }
            }}
            disabled={updateMutation.isPending}
            className="h-auto border-0 bg-transparent px-0 py-0 text-lg font-semibold shadow-none focus-visible:border-0 focus-visible:ring-0"
            aria-label="Recording title"
          />
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {rec.createdAt ? (
              <span>{format(new Date(rec.createdAt), "PPpp")}</span>
            ) : null}
            <span>{formatDuration(rec.audioDuration)}</span>
          </div>
          <div className="mt-4 max-w-xs space-y-2">
            <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
              Site
            </p>
            {managedSitesQuery.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select
                value={selectedSiteValue}
                onValueChange={saveSite}
                disabled={updateMutation.isPending}
              >
                <SelectTrigger aria-label="Assign note to site">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={GENERAL_NOTE_VALUE}>
                    General note
                  </SelectItem>
                  {(managedSitesQuery.data ?? []).map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge
            variant={
              rec.status === "ready" || rec.status === "approved"
                ? "gray"
                : rec.status === "failed" || rec.status === "rejected"
                  ? "destructive"
                  : "secondary"
            }
          >
            {rec.status === "pending_approval"
              ? "pending approval"
              : rec.status}
          </Badge>

          {/* Delete */}
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete recording</DialogTitle>
                <DialogDescription>
                  This will permanently delete the recording and its audio file.
                  This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setDeleteOpen(false)}
                  disabled={deleteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteMutation.mutate({ id: rec.id })}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Audio player */}
      <Card>
        <CardContent className="pt-4">
          {/** biome-ignore lint/a11y/useMediaCaption: Audio player for recording */}
          <audio
            controls
            src={audioSrc}
            className="w-full"
            preload="metadata"
          />
        </CardContent>
      </Card>

      {/* AI Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">AI Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {isProcessing ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : rec.aiSummary ? (
            <>
              <p className="text-sm leading-relaxed">{rec.aiSummary}</p>
              {isPendingApproval && (
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      approveSummaryMutation.mutate({ id: rec.id })
                    }
                    disabled={
                      approveSummaryMutation.isPending ||
                      rejectSummaryMutation.isPending
                    }
                  >
                    Approve for audit log
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => rejectSummaryMutation.mutate({ id: rec.id })}
                    disabled={
                      approveSummaryMutation.isPending ||
                      rejectSummaryMutation.isPending
                    }
                  >
                    Reject
                  </Button>
                </div>
              )}
              {rec.status === "approved" && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Approved — recorded in audit log.
                </p>
              )}
              {rec.status === "rejected" && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Rejected — not included in audit log.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No summary available.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Transcription */}
      <Card>
        <CardHeader className="flex w-full flex-row items-center justify-between gap-3 pb-2">
          <CardTitle className="min-w-0 flex-1 text-sm font-medium">
            Transcription
          </CardTitle>
          {rec.transcription && (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto gap-2"
              onClick={() => {
                navigator.clipboard.writeText(rec.transcription ?? "");
                toast.success("Copied to clipboard");
              }}
            >
              <ClipboardCopy className="size-4" />
              <span>Copy transcript</span>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isProcessing ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : rec.transcription ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
              {rec.transcription}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {rec.status === "failed"
                ? "Transcription failed. The audio may be too short or unclear."
                : "No transcription available."}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
